import * as React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { brand } from '../../shared-theme/brand';

const faqs = [
  {
    question: 'What practice areas does OpenIntake support?',
    answer: 'All of them. Firms use it for personal injury, family law, criminal defense, estate planning, corporate work, and more. Your agent is set up for the specific areas your firm takes on.',
  },
  {
    question: 'Will callers know they are talking to AI?',
    answer: "Most don't. The voice is natural, it handles interruptions and follow-up questions, and it stays calm with callers who are upset. Hit \"Hear a sample call\" above to judge for yourself.",
  },
  {
    question: 'How long does setup take?',
    answer: 'Usually one to two weeks. We configure your practice areas, intake questions, and calendar, and your onboarding contact walks you through testing before anything goes live.',
  },
  {
    question: 'Which channels does it cover besides phone calls?',
    answer: "Inbound and outbound calls, two-way texting from your firm's verified numbers, web chat on your site, and dynamic intake forms. Web leads get a call back, typically in under 15 seconds.",
  },
  {
    question: 'Which tools does it integrate with?',
    answer: 'Clio, Filevine, Litify, MyCase, Salesforce, HubSpot, Lawmatics, CasePeer, LeadDocket, SmartAdvocate, and Zapier, plus your phone system and calendar. New leads, transcripts, and booked consults go straight into the systems you already use.',
  },
  {
    question: 'Who maintains it after launch?',
    answer: "We do. Our team watches performance, tunes your qualification flows, and ships improvements. There's no software for your staff to learn, and you always have a named contact.",
  },
];

export default function FAQ() {
  const [expanded, setExpanded] = React.useState<number | false>(0);

  return (
    <Box
      id="faq"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: 'background.default',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '0.8fr 1.2fr' },
            gap: { xs: 4, md: 10 },
            alignItems: 'start',
          }}
        >
          <Box sx={{ position: { md: 'sticky' }, top: { md: 120 } }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 500,
                letterSpacing: '-0.025em',
                lineHeight: 1.12,
                color: brand.brown,
                textWrap: 'balance',
                mb: 2,
              }}
            >
              Questions firms ask us
            </Typography>
            <Typography sx={{ color: brand.taupe, lineHeight: 1.65 }}>
              Something else on your mind? Email{' '}
              <Link
                href="mailto:info@openintake.ai"
                sx={{ color: brand.brown, textDecorationColor: brand.khaki, fontWeight: 500 }}
              >
                info@openintake.ai
              </Link>
              .
            </Typography>
          </Box>

          <Box sx={{ borderTop: `1px solid ${brand.sand}` }}>
            {faqs.map((faq, index) => (
              <Accordion
                key={faq.question}
                disableGutters
                elevation={0}
                square
                expanded={expanded === index}
                onChange={(_, isExpanded) => setExpanded(isExpanded ? index : false)}
                sx={{
                  backgroundColor: 'transparent',
                  border: 0,
                  borderBottom: `1px solid ${brand.sand}`,
                  borderRadius: '0 !important',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<AddIcon sx={{ color: brand.copper, fontSize: 22 }} />}
                  aria-controls={`faq${index}-content`}
                  id={`faq${index}-header`}
                  sx={{
                    px: 0,
                    py: 1.25,
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': { transform: 'rotate(45deg)' },
                    '&:hover .faq-q': { color: brand.copper },
                  }}
                >
                  <Typography
                    component="h3"
                    className="faq-q"
                    sx={{
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      fontWeight: 600,
                      letterSpacing: '-0.01em',
                      color: brand.brown,
                      pr: 2,
                      transition: 'color 0.15s ease',
                    }}
                  >
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0, pb: 3 }}>
                  <Typography sx={{ color: brand.taupe, lineHeight: 1.7, maxWidth: 620 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
